/**
 * `/admin/settings` — Profil personnel du Super Admin.
 *
 * Permet de modifier son propre e-mail (avec confirmation par mot de passe)
 * et de changer son mot de passe. Le Super Admin ne peut PAS se rétrograder
 * lui-même — cette contrainte est appliquée côté backend.
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminPageHeader, AdminCard } from "./ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettingsPage() {
  const { user, refresh } = useAuth();
  return (
    <div className="animate-fade-up max-w-3xl">
      <AdminPageHeader
        overline="Mon compte"
        title="Paramètres du Super Administrateur"
        description="Modifiez votre e-mail et votre mot de passe. Tous les changements sont journalisés dans l'audit trail."
        testId="admin-settings-header"
      />

      <div className="space-y-6">
        <AdminCard className="p-5" data-testid="admin-settings-profile">
          <div className="flex items-center gap-2 text-white/80 mb-4">
            <User className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold">Identité</h2>
          </div>
          <ProfileForm user={user} onSaved={refresh} />
        </AdminCard>

        <AdminCard className="p-5" data-testid="admin-settings-email">
          <div className="flex items-center gap-2 text-white/80 mb-4">
            <Mail className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold">Adresse e-mail</h2>
          </div>
          <ChangeEmailForm current={user?.email} onSaved={refresh} />
        </AdminCard>

        <AdminCard className="p-5" data-testid="admin-settings-password">
          <div className="flex items-center gap-2 text-white/80 mb-4">
            <KeyRound className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold">Mot de passe</h2>
          </div>
          <ChangePasswordForm />
        </AdminCard>
      </div>
    </div>
  );
}

function ProfileForm({ user, onSaved }) {
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me", form);
      toast.success("Profil mis à jour");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
      <div>
        <Label className="text-xs">Prénom</Label>
        <Input value={form.first_name}
               onChange={(e) => setForm({ ...form, first_name: e.target.value })}
               data-testid="admin-settings-firstname" />
      </div>
      <div>
        <Label className="text-xs">Nom</Label>
        <Input value={form.last_name}
               onChange={(e) => setForm({ ...form, last_name: e.target.value })}
               data-testid="admin-settings-lastname" />
      </div>
      <div className="sm:col-span-2 flex justify-end">
        <Button type="submit" disabled={saving}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="admin-settings-profile-save">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>
    </form>
  );
}

function ChangeEmailForm({ current, onSaved }) {
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail, current_password: password });
      toast.success("E-mail mis à jour");
      setNewEmail(""); setPassword("");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label className="text-xs">Adresse actuelle</Label>
        <div className="mt-1 font-mono text-sm text-white/80">{current}</div>
      </div>
      <div>
        <Label className="text-xs">Nouvelle adresse</Label>
        <Input type="email" value={newEmail}
               onChange={(e) => setNewEmail(e.target.value)}
               placeholder="nouveau@exemple.fr" required
               data-testid="admin-settings-new-email" />
      </div>
      <div>
        <Label className="text-xs">Mot de passe actuel (confirmation)</Label>
        <Input type="password" value={password}
               onChange={(e) => setPassword(e.target.value)}
               placeholder="••••••••" required
               data-testid="admin-settings-email-current-password" />
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={saving}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="admin-settings-email-save">
          {saving ? "Enregistrement…" : "Changer d'e-mail"}
        </Button>
      </div>
    </form>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirmation) {
      toast.error("Les deux mots de passe ne correspondent pas");
      return;
    }
    if (next.length < 10) {
      toast.error("Utilisez au moins 10 caractères");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe changé");
      setCurrent(""); setNext(""); setConfirmation("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <Label className="text-xs">Mot de passe actuel</Label>
        <Input type="password" value={current}
               onChange={(e) => setCurrent(e.target.value)}
               placeholder="••••••••" required
               data-testid="admin-settings-current-password" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Nouveau mot de passe</Label>
          <Input type="password" value={next}
                 onChange={(e) => setNext(e.target.value)}
                 placeholder="Min. 10 caractères" required
                 data-testid="admin-settings-new-password" />
        </div>
        <div>
          <Label className="text-xs">Confirmez le nouveau mot de passe</Label>
          <Input type="password" value={confirmation}
                 onChange={(e) => setConfirmation(e.target.value)}
                 placeholder="Répétez" required
                 data-testid="admin-settings-confirm-password" />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={saving}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                data-testid="admin-settings-password-save">
          {saving ? "Enregistrement…" : "Changer le mot de passe"}
        </Button>
      </div>
    </form>
  );
}
